import React, {useEffect} from "react";
import styled from "styled-components";
import {useForm} from "react-hook-form";
import {useAtom} from "jotai";
import {useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import {selAdminInfo, updateAdmin} from "../../services/Platform/ManageAdminAxios";
import {PwChange} from "./UserDetail";
import {modalController} from "../../store";
import {adminInfoAtom} from "./entity/Admin";

function PlatformAdminDetail() {
  const [, setModal] = useAtom(modalController);
  const navigate = useNavigate();
  const [adminInfoState, setAdminInfoState] = useAtom(adminInfoAtom);
  const {register, handleSubmit, reset, formState: {errors}} = useForm({
    mode: "onSubmit",
    defaultValues: adminInfoState
  });

  const onError = error => console.log(error);

  useEffect(() => {
    selAdminInfo().then(response => {
      if (response) {
        const adminInfo = {
          ...response,
          activeYn: response.status === "NORMAL" ? "Y" : "N"
        };
        setAdminInfoState(adminInfo);
        reset(adminInfo);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleManagerPhone = event => {
    setAdminInfoState({
      ...adminInfoState,
      phoneNumber: event.target.value
    });
  };

  const onSubmit = () => {
    updateAdmin(adminInfoState).then(response => {
      if (response) {
        toast.success("정보 변경이 완료되었습니다.", {toastId: "updateAdmin"});
        toast.onChange(payload => {
          if (payload.status === "removed" && payload.type !== toast.TYPE.ERROR && payload.id === "updateAdmin") {
            navigate("/board/dashboard");
          }
        });
      } else {
        toast.warning("어드민 계정 수정에 실패하였습니다.");
      }
    });
  };

  const onModalPw = () => {
    setModal({
      isShow: false,
      modalComponent: null
    });
  };

  const handleSavePassword = data => {
    updateAdmin(data).then(response => {
      if (response) {
        onModalPw();
        toast.success("비밀번호 변경이 완료되었습니다.");
      } else {
        toast.warning("수정에 실패하였습니다. 관리자에게 문의하세요.");
      }
    });
  };

  const isActive = ["NORMAL", "Y"].includes(adminInfoState?.status);
  const roleLabel = adminInfoState?.role === "SUPER_ADMIN" ? "최고 관리자" : "관리자";

  return (
      <AdminPage>
        <AdminForm onSubmit={handleSubmit(onSubmit, onError)}>
          <PageTitle>
            <h1>나의 정보</h1>
            <p>관리자 계정과 담당자 정보를 확인하고 관리합니다.</p>
          </PageTitle>

          {adminInfoState && (
              <AdminContent>
                <AdminHero>
                  <HeroCopy>
                    <Eyebrow>ADMIN MY PAGE</Eyebrow>
                    <HeroTitle>관리자 계정 정보를 안전하게 관리하세요.</HeroTitle>
                    <HeroDescription>로그인 계정과 담당자 연락처를 한 화면에서 확인하고 변경할 수 있습니다.</HeroDescription>
                  </HeroCopy>
                  <SummaryGrid>
                    <SummaryItem>
                      <SummaryLabel>계정 상태</SummaryLabel>
                      <SummaryValue $active={isActive}>{isActive ? "활성화" : "비활성화"}</SummaryValue>
                    </SummaryItem>
                    <SummaryItem>
                      <SummaryLabel>권한</SummaryLabel>
                      <SummaryValue>{roleLabel}</SummaryValue>
                    </SummaryItem>
                    <SummaryItem $wide>
                      <SummaryLabel>로그인 아이디</SummaryLabel>
                      <SummaryValue title={adminInfoState.email}>{adminInfoState.email || "-"}</SummaryValue>
                    </SummaryItem>
                  </SummaryGrid>
                </AdminHero>

                <InfoGrid>
                  <InfoCard>
                    <CardHeader>
                      <CardIcon aria-hidden="true">ID</CardIcon>
                      <div>
                        <CardTitle>기본 정보</CardTitle>
                        <CardDescription>로그인 계정과 보안 설정을 관리합니다.</CardDescription>
                      </div>
                    </CardHeader>
                    <CardBody>
                      <Field>
                        <FieldLabel>아이디</FieldLabel>
                        <StyledInput
                            type="text"
                            aria-label="아이디"
                            value={adminInfoState.email || ""}
                            readOnly
                        />
                        <FieldHelp>로그인 아이디는 변경할 수 없습니다.</FieldHelp>
                      </Field>
                      <SecurityRow>
                        <div>
                          <SecurityTitle>비밀번호</SecurityTitle>
                          <SecurityDescription>주기적인 비밀번호 변경으로 계정을 보호하세요.</SecurityDescription>
                        </div>
                        <PasswordAction>
                          <PwChange title="비밀번호 변경" modalInfo="ADMIN" onSave={handleSavePassword} onSubmit={onModalPw}/>
                        </PasswordAction>
                      </SecurityRow>
                    </CardBody>
                  </InfoCard>

                  <InfoCard>
                    <CardHeader>
                      <CardIcon aria-hidden="true">MY</CardIcon>
                      <div>
                        <CardTitle>담당자 정보</CardTitle>
                        <CardDescription>서비스 운영에 사용하는 담당자 정보를 확인합니다.</CardDescription>
                      </div>
                    </CardHeader>
                    <CardBody>
                      <Field>
                        <FieldLabel>담당자명</FieldLabel>
                        <StyledInput
                            type="text"
                            aria-label="담당자명"
                            value={adminInfoState.name || ""}
                            readOnly
                        />
                      </Field>
                      <Field>
                        <FieldLabel>담당자 연락처</FieldLabel>
                        <StyledInput
                            type="text"
                            aria-label="담당자 연락처"
                            placeholder="연락처를 입력해주세요."
                            $invalid={Boolean(errors.phoneNumber)}
                            {...register("phoneNumber", {
                              required: "담당자 연락처를 입력해주세요.",
                              pattern: {
                                value: /^[0-9-]+$/g,
                                message: "숫자와 하이픈(-)만 입력해주세요."
                              },
                              onChange: handleManagerPhone
                            })}
                            value={adminInfoState.phoneNumber || ""}
                        />
                        {errors.phoneNumber
                          ? <ValidationMessage>{errors.phoneNumber.message}</ValidationMessage>
                          : <FieldHelp>숫자와 하이픈(-)을 사용할 수 있습니다.</FieldHelp>}
                      </Field>
                    </CardBody>
                  </InfoCard>
                </InfoGrid>

                <ActionBar>
                  <ActionGuide>
                    <strong>관리자 정보 설정</strong>
                    <span>변경한 연락처를 확인한 뒤 저장해 주세요.</span>
                  </ActionGuide>
                  <SaveButton type="submit">변경사항 저장</SaveButton>
                </ActionBar>
              </AdminContent>
          )}
        </AdminForm>
      </AdminPage>
  );
}

export default PlatformAdminDetail;

const AdminPage = styled.main`
  width: 100%;
`;

const AdminForm = styled.form`
  width: 100%;
  box-sizing: border-box;
  padding: 24px 28px 32px;

  @media (max-width: 960px) {
    padding: 20px 18px 28px;
  }

  @media (max-width: 620px) {
    padding: 16px 12px 24px;
  }
`;

const PageTitle = styled.div`
  min-height: 58px;

  h1 {
    margin: 0;
    color: #20242b;
    font-size: var(--font-size-24);
    font-weight: 750;
    letter-spacing: -0.025em;
  }

  p {
    margin: 6px 0 0;
    color: #7b838c;
    font-size: var(--font-size-13);
  }
`;

const AdminContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AdminHero = styled.section`
  position: relative;
  display: grid;
  grid-template-columns: minmax(260px, 0.9fr) minmax(0, 1.35fr);
  align-items: center;
  gap: 24px;
  padding: 22px;
  overflow: hidden;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  background: linear-gradient(135deg, #fff8f2 0%, #fff 60%);
  box-shadow: 0 5px 16px rgba(31, 41, 55, 0.04);

  &::before {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 3px;
    background: linear-gradient(90deg, #f5811f, #ffae68);
    content: "";
  }

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const HeroCopy = styled.div``;
const Eyebrow = styled.div`
  margin-bottom: 7px;
  color: #f5811f;
  font-size: var(--font-size-12);
  font-weight: 800;
  letter-spacing: 0.14em;
`;
const HeroTitle = styled.h2`
  margin: 0;
  color: #242a31;
  font-size: var(--font-size-20);
  line-height: 1.35;
  letter-spacing: -0.03em;
`;
const HeroDescription = styled.p`
  margin: 7px 0 0;
  color: #737c85;
  font-size: var(--font-size-13);
  line-height: 1.55;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 0.75fr 0.75fr 1.5fr;
  gap: 9px;

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;
const SummaryItem = styled.div`
  min-width: 0;
  padding: 12px 13px;
  border: 1px solid #eceff2;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.9);

  @media (max-width: 560px) {
    grid-column: ${({$wide}) => $wide ? "1 / -1" : "auto"};
  }
`;
const SummaryLabel = styled.div`
  margin-bottom: 6px;
  color: #818991;
  font-size: var(--font-size-12);
`;
const SummaryValue = styled.div`
  overflow: hidden;
  color: ${({$active}) => $active ? "#318553" : "#30363d"};
  font-size: var(--font-size-15);
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;
const InfoCard = styled.section`
  overflow: hidden;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 5px 18px rgba(31, 41, 55, 0.045);
`;
const CardHeader = styled.header`
  display: flex;
  min-height: 72px;
  box-sizing: border-box;
  align-items: center;
  gap: 11px;
  padding: 15px 18px;
  border-bottom: 1px solid #edf0f2;
  background: #f9fafb;
`;
const CardIcon = styled.span`
  display: inline-flex;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: #fff0e3;
  color: #f5811f;
  font-size: var(--font-size-12);
  font-weight: 900;
`;
const CardTitle = styled.div`
  color: #252b31;
  font-size: var(--font-size-16);
  font-weight: 800;
`;
const CardDescription = styled.div`
  margin-top: 4px;
  color: #858d95;
  font-size: var(--font-size-12);
`;
const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 20px;
`;
const Field = styled.label`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 7px;
`;
const FieldLabel = styled.span`
  color: #454c53;
  font-size: var(--font-size-13);
  font-weight: 700;
`;
const StyledInput = styled.input`
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  padding: 0 13px;
  border: 1px solid ${({$invalid}) => $invalid ? "#dc6c6c" : "#dfe3e7"};
  border-radius: 7px;
  outline: 0;
  background: ${({readOnly}) => readOnly ? "#f4f6f7" : "#fff"};
  color: ${({readOnly}) => readOnly ? "#747c84" : "#30363d"};
  font-family: inherit;
  font-size: var(--font-size-14);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: #f5811f;
    box-shadow: 0 0 0 3px rgba(245, 129, 31, 0.1);
  }
`;
const FieldHelp = styled.span`
  color: #969da4;
  font-size: var(--font-size-11);
`;
const ValidationMessage = styled.span`
  color: #d34c4c;
  font-size: var(--font-size-11);
`;
const SecurityRow = styled.div`
  display: flex;
  min-height: 63px;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 13px 14px;
  border: 1px solid #ebeef0;
  border-radius: 8px;
  background: #fafbfc;

  @media (max-width: 520px) {
    align-items: stretch;
    flex-direction: column;
  }
`;
const SecurityTitle = styled.div`
  color: #424950;
  font-size: var(--font-size-13);
  font-weight: 800;
`;
const SecurityDescription = styled.div`
  margin-top: 5px;
  color: #8b9299;
  font-size: var(--font-size-11);
  line-height: 1.45;
`;
const PasswordAction = styled.div`
  flex: 0 0 auto;

  button {
    min-width: 104px;
    height: 34px;
    padding: 0 13px;
    border: 1px solid #f1c8a7;
    border-radius: 6px;
    background: #fff;
    color: #e87518;
    font-size: var(--font-size-12);
    font-weight: 700;
    cursor: pointer;
  }

  button:hover {
    background: #fff6ee;
  }
`;
const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 17px 20px;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 5px 16px rgba(31, 41, 55, 0.04);

  @media (max-width: 620px) {
    align-items: stretch;
    flex-direction: column;
  }
`;
const ActionGuide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  strong {
    color: #353c43;
    font-size: var(--font-size-14);
  }

  span {
    color: #899098;
    font-size: var(--font-size-12);
  }
`;
const SaveButton = styled.button`
  min-width: 124px;
  height: 38px;
  padding: 0 18px;
  border: 1px solid #f5811f;
  border-radius: 7px;
  background: #f5811f;
  color: #fff;
  font-family: inherit;
  font-size: var(--font-size-13);
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 5px 12px rgba(245, 129, 31, 0.2);

  &:hover {
    background: #df7017;
  }
`;
